import { dateUtils, citizenshipEntity } from 'shared';
import dayjs from 'dayjs';
import type { Airflow } from '@aviata/platform-types';
import type { MarkedProduct, ProductState } from '@aviata/products-fns';
import type { PassengerKey, PassengersData } from '@aviata/booking-data';

import { roamingConfig } from '@/widgets/roaming';

import { unselectedState } from './config';
import type {
  GetVisibleProductsProps,
  ShouldShowProductProps,
  SelectedStateProps,
} from './types';

function getSelectedState({
  product,
  selectedAsPackage = false,
  productState,
}: SelectedStateProps) {
  return {
    ...productState,
    id: product.id,
    price: Number(product.price.amount),
    selectedAsPackage,
  };
}

function getUnselectedState(state?: ProductState) {
  return {
    ...state,
    ...unselectedState,
  };
}

function isInsuranceAvailable(passengers: PassengersData) {
  return (
    isEveryKZCitizen(passengers) && isEveryPassengerUnderSeventy(passengers)
  );
}

function shouldShowProduct({
  state,
  serviceName,
  passengers,
}: ShouldShowProductProps) {
  const serviceState = state[serviceName];
  if (
    !serviceState ||
    serviceState.selectedAsPackage ||
    serviceName === 'medical-insurance' ||
    serviceName === 'flight-delay-insurance'
  ) {
    return false;
  }

  if (serviceName === 'insurance') {
    return isInsuranceAvailable(passengers);
  }
  if (serviceName === roamingConfig.ROAMING) {
    return false;
  }
  return true;
}

function getVisiblePackages(
  packages: Airflow.Products.Package[],
  passengers: PassengersData,
) {
  return packages.filter(productsPackage =>
    productsPackage.products.every(product => {
      if (product.service === 'insurance') {
        return isInsuranceAvailable(passengers);
      }
      return true;
    }),
  );
}

function getVisibleProducts({
  state,
  passengers,
  products,
}: GetVisibleProductsProps) {
  return products.filter(product =>
    shouldShowProduct({ state, passengers, serviceName: product.service }),
  );
}

function isEveryPassengerUnderSeventy(passengers: PassengersData) {
  const passengersKeys = Object.keys(passengers) as PassengerKey[];
  return passengersKeys.every(key => {
    const dob = passengers[key]?.dob;
    if (dob) {
      return getAge(dob, dateUtils.format(dayjs(), 'YYYY-MM-DD')) < 70;
    }
    return false;
  });
}

function isEveryKZCitizen(passengers: PassengersData) {
  const passengersKeys = Object.keys(passengers) as PassengerKey[];
  return passengersKeys.every(
    key =>
      passengers[key]?.citizenship === citizenshipEntity.config.KAZAKHSTAN_CODE,
  );
}

function getPackageInfo(productPackage: Airflow.Products.Package) {
  return {
    id: productPackage.id,
    title: productPackage.title,
    price: Number(productPackage.price.amount),
    type: productPackage.type,
  };
}

function getAge(birthDate: string, currentDate: string) {
  /**
   * Parse date in a few formats
   * @see https://day.js.org/docs/en/parse/string-format
   */
  const birth = dayjs(birthDate, ['DD.MM.YYYY', 'DD-MM-YYYY']);
  return dayjs(currentDate).diff(birth, 'y');
}

function getMedicalInsuranceProduct(products: MarkedProduct[]) {
  return products.filter(product => product.service === 'medical-insurance');
}

export const productsLib = {
  getSelectedState,
  getUnselectedState,
  getPackageInfo,
  getVisibleProducts,
  getVisiblePackages,
  getMedicalInsuranceProduct,
};
